import { CourseServiceType, DonationCourse, ExhibitionProject, FormPaymentMethod, isReward, Project, RewardCourse } from "interfaces/front/business";
import { TippingOutput } from "./service/client/FrontOrderService";
import { Order, OrderDetailGroup } from "interfaces/front/page/orderThanks";
import { SERVICE_TYPES } from "lib/constants";

// TODO: lib/google/analytics/enhancedEcommerce.ts として移動したい。

export interface CartItem {
  course: RewardCourse | DonationCourse | Aniale;
  quantity: number;
  price?: number;
}

export interface Aniale {
  id: number;
  service_type: CourseServiceType;
  type: "donation";
}

export const ecommerceDataForProject = (project: ExhibitionProject): Record<string, unknown> => {
  return {
    event: "viewProject",
    pageTitle: project.marketing_content.seo_title,
    ecommerce: {
      detail: {
        products: [
          {
            name: `${project.code}`,
            id: `${project.code}`,
            category: "anime",
          },
        ],
      },
    },
  };
};

export const ecommerceDataForOrderForm = (slug: string, cartItems: CartItem[]): Record<string, unknown> => {
  const products = cartItems.map((cartItem) => productDataFromCourse(slug, cartItem));
  return {
    event: "addToCart",
    ecommerce: {
      currencyCode: "JPY",
      add: { products },
    },
  };
};

export const ecommerceDataForPayment = (slug: string, cartItems: CartItem[], paymentMethod: FormPaymentMethod): Record<string, unknown> => {
  const products = cartItems.map((cartItem) => productDataFromCourse(slug, cartItem));
  return {
    event: "checkout",
    ecommerce: {
      checkout: {
        actionField: { step: 1, option: paymentMethod },
        products,
      },
    },
  };
};

export const ecommerceDataForRecipient = (slug: string, cartItems: CartItem[]): Record<string, unknown> => {
  const products = cartItems.map((cartItem) => productDataFromCourse(slug, cartItem));
  return {
    event: "checkout",
    ecommerce: {
      checkout: {
        actionField: { step: 2 },
        products,
      },
    },
  };
};

export const ecommerceDataForOrderThanks = (
  order: Pick<Order, "code" | "order_payment"> & { project: Pick<Project, "code"> },
  orderDetailGroups: OrderDetailGroup[]
): Record<string, unknown> => {
  const products = orderDetailGroups.map((orderDetailGroup) => productDataFromOrder(order.project.code, orderDetailGroup));
  return {
    event: "orderThanks",
    ecommerce: {
      purchase: {
        actionField: {
          id: `${order.code}`,
          affiliation: "animefund",
          revenue: `${order.order_payment.amount}`,
        },
        products,
      },
    },
  };
};

export const ecommerceDataForTippingThanks = (
  project: Project,
  cartItems: CartItem[],
  tippingResult: TippingOutput,
  amount: number | undefined
): Record<string, unknown> => {
  const products = cartItems.map((cartItem) => productDataFromCourse(project.code, cartItem, amount));
  return {
    event: "orderThanks",
    ecommerce: {
      purchase: {
        actionField: {
          id: `${tippingResult.order_code}`,
          affiliation: "animefund",
          revenue: `${amount}`,
        },
        products,
      },
    },
  };
};

const productDataFromCourse = (slug: string, cartItem: CartItem, amount?: number): Record<string, unknown> => {
  const course = cartItem.course;
  const price = amount ?? cartItem.price;
  const serviceType = SERVICE_TYPES.find((type) => type.value === course.service_type)?.label
  const courseType = isReward(course) ? "ファンディング" : "アニエール";
  return {
    name: `${slug}`,
    id: `${slug}`,
    price: price,
    category: `${serviceType}/${courseType}`,
    variant: `${slug}-${course.id}`,
    quantity: cartItem.quantity,
  };
};

const productDataFromOrder = (projectCode: string, orderDetailGroup: OrderDetailGroup): Record<string, unknown> => {
  const serviceType = SERVICE_TYPES.find((type) => type.value === orderDetailGroup.course.service_type)?.label
  const courseType = isReward(orderDetailGroup.course) ? "ファンディング" : "アニエール";
  return {
    name: `${projectCode}`,
    id: `${projectCode}`,
    price: orderDetailGroup.orderDetails[0].price,
    category: `${serviceType}/${courseType}`,
    variant: `${projectCode}-${orderDetailGroup.course.id}`,
    quantity: orderDetailGroup.orderDetails.length,
  };
};
